import { useState, useCallback } from 'react';
import axiosInstance from '../services/axiosInstance';
import { API_ENDPOINTS } from '../services/api/apiConfig';

// Hook simple pour le cache de recherche de médecins
export function useCachedDoctorsSearch() {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache simple avec localStorage
  const getCacheKey = useCallback((query: string) => `doctors_search_${query}`, []);

  const getCachedSearch = useCallback((query: string) => {
    try {
      if (!query) return null;
      const cached = localStorage.getItem(getCacheKey(query));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [getCacheKey]);

  const setCachedSearch = useCallback((query: string, data: any) => {
    try {
      localStorage.setItem(getCacheKey(query), JSON.stringify(data));
      // Expirer après 10 minutes
      setTimeout(() => {
        localStorage.removeItem(getCacheKey(query));
      }, 10 * 60 * 1000);
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [getCacheKey]);

  const searchDoctors = useCallback(async (query: string, page: number = 1, size: number = 10) => {
    if (!query) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Vérifier le cache d'abord
      const cached = getCachedSearch(query);
      if (cached) {
        setResults(cached);
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(API_ENDPOINTS.USERS.SEARCH, {
        params: {
          role: "DOCTOR",
          query,
          page: page - 1,
          size,
        },
      });

      const doctorsData = response.data?.content || [];
      setResults(doctorsData);
      setCachedSearch(query, response.data);

    } catch (err) {
      setError("Erreur lors de la recherche de médecins");
      console.error("Error searching doctors:", err);
    } finally {
      setLoading(false);
    }
  }, [getCachedSearch, setCachedSearch]);

  const refetch = useCallback((query: string) => {
    // Invalider le cache et recharger
    localStorage.removeItem(getCacheKey(query));
    searchDoctors(query);
  }, [searchDoctors, getCacheKey]);

  return { results, loading, error, searchDoctors, refetch };
}
